/**
 * <cst-responsive-video>
 * Used by sections/cst-video.liquid
 *
 * WHY THIS EXISTS
 * ---------------
 * There is no reliable HTML-only way to serve a different video file per
 * breakpoint. The `media` attribute on <source> was removed from the spec and
 * browsers ignore it, and rendering two <video> elements and hiding one with
 * CSS still downloads both files. Liquid cannot help either, because Shopify
 * renders and caches one HTML response for every device.
 *
 * So the choice has to happen in the browser. This element receives both
 * candidates as inert <template> elements (a <template> is parsed but never
 * fetched), measures the viewport, and clones only the matching one into the
 * <video>. Exactly one file is ever downloaded.
 *
 * EXPECTED MARKUP
 *   <cst-responsive-video data-autoplay
 *                         data-poster-desktop="..."
 *                         data-poster-mobile="...">
 *     <video ...></video>
 *     <template data-cst-video-variant="desktop"><source ...></template>
 *     <template data-cst-video-variant="mobile"><source ...></template>  <- optional
 *   </cst-responsive-video>
 *
 * The mobile <template> is only rendered by Liquid when a mobile video was
 * actually uploaded. That absence *is* the fallback: with nothing to switch
 * to, this element keeps the desktop video on every screen.
 */

// Dawn treats 750px as the desktop breakpoint, so mobile is everything below
// it. Kept in one place; the matching media query lives in cst-video.css.
const CST_VIDEO_MOBILE_QUERY = '(max-width: 749px)';

// How early the file starts downloading, as a margin around the viewport.
const CST_VIDEO_PRELOAD_MARGIN = '200px 0px';

// Playback gate. The section has to be at least PLAY_RATIO visible before the
// video starts, and drop back to PAUSE_RATIO before it stops. Two different
// numbers give the gate hysteresis, so parking the scroll on the boundary
// cannot start and stop the video repeatedly.
const CST_VIDEO_PLAY_RATIO = 0.5;
const CST_VIDEO_PAUSE_RATIO = 0.25;

// A list, not a single number, so the observer keeps reporting as the section
// travels through the viewport instead of firing once on the way in.
const CST_VIDEO_PLAY_THRESHOLDS = [0, 0.1, 0.25, 0.4, 0.5, 0.6, 0.75, 1];

// Only a real pointer can hover. Touch screens report neither, and would be
// left with controls that can never be summoned.
const CST_VIDEO_HOVER_QUERY = '(hover: hover) and (pointer: fine)';

// The gestures browsers accept as "the visitor has interacted with the page",
// which is the gate that unmuting has to wait for.
const CST_VIDEO_GESTURES = ['pointerdown', 'keydown', 'touchstart'];

if (!customElements.get('cst-responsive-video')) {
  customElements.define(
    'cst-responsive-video',
    class CstResponsiveVideo extends HTMLElement {
      constructor() {
        super();
        this.loadedVariant = null;
        // Whether the section is currently on screen enough to be playing.
        this.shouldPlay = false;
        // Has this video ever started, and did we stop it ourselves? Together
        // these tell a scroll-away pause apart from a visitor's own pause, so
        // scrolling back never overrides a deliberate stop.
        this.hasPlayed = false;
        this.gatePaused = false;
        // Controls we are not allowed to take away again.
        this.controlsLocked = false;
        // Set on devices that cannot hover, where the controls follow
        // visibility instead of the pointer.
        this.controlsOnView = false;
        // Whether the visitor has actually scrolled. "Play when scrolled to"
        // is a requirement about time, not only about position, and geometry
        // alone cannot express it - see handleFirstScroll.
        this.hasScrolled = false;
        this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
        this.handleFirstScroll = this.handleFirstScroll.bind(this);
        this.showControls = this.showControls.bind(this);
        this.hideControls = this.hideControls.bind(this);
      }

      // Runs when the element enters the DOM. In the theme editor Shopify
      // replaces the whole section on every setting change, which destroys and
      // re-creates this element, so connectedCallback doubles as our
      // "shopify:section:load" handler for free.
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        this.mobileQuery = window.matchMedia(CST_VIDEO_MOBILE_QUERY);
        this.autoplay = this.hasAttribute('data-autoplay');
        this.wantsSound = this.hasAttribute('data-sound');

        // Accessibility: honour the visitor's OS-level "reduce motion" setting.
        // Motion they did not ask for is turned into a video they can start.
        if (this.autoplay && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.autoplay = false;
          this.video.removeAttribute('autoplay');
          this.revealControls();
        }

        // Controls on hover. This lives in JS because `controls` is an
        // attribute rather than a style, so CSS :hover cannot reach it.
        if (this.hasAttribute('data-controls-on-hover')) {
          if (window.matchMedia(CST_VIDEO_HOVER_QUERY).matches) {
            this.addEventListener('mouseenter', this.showControls);
            this.addEventListener('mouseleave', this.hideControls);
          } else {
            // No pointer to hover with. Tie the controls to visibility
            // instead, which carries the same intent: they appear once the
            // visitor has actually reached the video.
            this.controlsOnView = true;
          }
        }

        // Downloading and playing are deliberately two different moments.
        //
        // The file is fetched while the section is still approaching, so it is
        // ready by the time it is wanted. Playback, though, waits until the
        // section is genuinely on screen: this section can sit just inside the
        // fold on page load, and starting there would be an autoplay the
        // visitor never scrolled to.
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.loadVariant(this.variantForViewport());
            });
          },
          { rootMargin: CST_VIDEO_PRELOAD_MARGIN }
        );
        this.observer.observe(this);

        // The playback gate, measured against the real viewport (no margin).
        this.playObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => this.handleVisibility(entry));
          },
          { threshold: CST_VIDEO_PLAY_THRESHOLDS }
        );
        this.playObserver.observe(this);

        // A scroll has to have happened before the video may start. On a tall
        // screen this section can already be most of the way into view at page
        // load - a portrait tablet sees nearly all of it - and visibility
        // alone would read that as "scrolled to". Waiting for the scroll makes
        // the rule hold on every viewport, not just the ones where the
        // geometry happens to agree.
        window.addEventListener('scroll', this.handleFirstScroll, { passive: true, once: true });

        // Crossing the breakpoint - a real resize, a rotated phone, or the
        // device toggle in the theme editor - may mean a different file.
        this.mobileQuery.addEventListener('change', this.handleBreakpointChange);
      }

      // Always clean up, or every theme-editor edit would leak an observer and
      // a media-query listener.
      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.playObserver) this.playObserver.disconnect();
        if (this.mobileQuery) {
          this.mobileQuery.removeEventListener('change', this.handleBreakpointChange);
        }
        this.removeEventListener('mouseenter', this.showControls);
        this.removeEventListener('mouseleave', this.hideControls);
        window.removeEventListener('scroll', this.handleFirstScroll);
      }

      /**
       * Which variant this viewport should get.
       * Mobile is only chosen when a mobile <template> was rendered - that is
       * the "no mobile video uploaded, use the desktop one" rule.
       */
      variantForViewport() {
        if (this.mobileQuery.matches && this.template('mobile')) return 'mobile';
        return 'desktop';
      }

      template(variant) {
        return this.querySelector(`template[data-cst-video-variant="${variant}"]`);
      }

      /**
       * Move the chosen variant's <source> into the <video> and start loading.
       * No-op when that variant is already loaded, so a resize inside the same
       * breakpoint never restarts playback.
       */
      loadVariant(variant) {
        if (variant === this.loadedVariant) return;

        const template = this.template(variant);
        if (!template) return;

        const wasPlaying = this.loadedVariant !== null && !this.video.paused;

        // Swap the source list.
        this.video.querySelectorAll('source').forEach((source) => source.remove());
        this.video.appendChild(template.content.cloneNode(true));

        const poster = variant === 'mobile' ? this.dataset.posterMobile : this.dataset.posterDesktop;
        if (poster) this.video.setAttribute('poster', poster);

        this.loadedVariant = variant;

        // load() applies the new <source> list. It also resets the element, so
        // resume playback if we interrupted it mid-play.
        this.video.load();
        // Only resume if the gate allows it, or if we interrupted a video that
        // was already running when the breakpoint changed under it.
        if (wasPlaying || this.mayPlay()) this.play();
      }

      /**
       * Open or close the playback gate for the section's current visibility.
       *
       * The ratio is measured against whichever is smaller - the section or
       * the viewport - because a video taller than the screen can never reach
       * a 50% intersectionRatio, and would otherwise never play at all.
       */
      handleVisibility(entry) {
        // Without autoplay the visitor is in charge; never touch their video.
        if (!this.autoplay) return;

        const viewport = window.innerHeight || document.documentElement.clientHeight;
        const reference = Math.min(entry.boundingClientRect.height, viewport);
        const visible = reference > 0 ? entry.intersectionRect.height / reference : 0;

        if (!this.shouldPlay && visible >= CST_VIDEO_PLAY_RATIO) {
          this.shouldPlay = true;
          // The file may still be in flight; loadVariant() plays on arrival.
          if (this.controlsOnView) this.showControls();
          if (this.mayPlay()) this.play();
        } else if (this.shouldPlay && visible <= CST_VIDEO_PAUSE_RATIO) {
          this.shouldPlay = false;
          if (this.controlsOnView) this.hideControls();
          // Only claim the pause as ours if the video was actually running.
          // An already-paused video means the visitor stopped it, and that
          // must not be undone the next time the section scrolls back.
          if (this.loadedVariant && !this.video.paused) {
            this.video.pause();
            this.gatePaused = true;
          }
        }
      }

      handleBreakpointChange() {
        // Only act once the video is actually in play; otherwise the
        // IntersectionObserver will pick the right variant when it matters.
        if (this.loadedVariant === null) return;
        this.loadVariant(this.variantForViewport());
      }

      /**
       * May the gate start the video right now?
       *
       * A video we paused ourselves resumes freely. A video that has never
       * played also needs a real scroll to have happened, so that a section
       * sitting inside the fold at page load stays still. A video the visitor
       * paused is never restarted from here.
       */
      mayPlay() {
        if (!this.autoplay || !this.shouldPlay || !this.loadedVariant) return false;
        if (this.gatePaused) return true;
        return !this.hasPlayed && this.hasScrolled;
      }

      // The gate may have been open since page load, waiting only for this.
      handleFirstScroll() {
        this.hasScrolled = true;
        if (this.mayPlay()) this.play();
      }

      /**
       * Start the video, negotiating sound down until the browser agrees.
       *
       * Sound is tried first. If it is refused - which is the norm until the
       * visitor has interacted with the page - we retry muted, which is always
       * allowed, and arrange to unmute at their first gesture. Only if silent
       * playback is refused too do we give up and show a play button.
       */
      play() {
        this.hasPlayed = true;
        this.gatePaused = false;
        this.video.muted = !this.wantsSound;

        this.attemptPlay().catch(() => {
          if (this.video.muted) {
            // Even silent playback was refused (data-saver, or a strict
            // setting). A frozen frame is worse than an obvious play button.
            this.revealControls();
            return;
          }

          this.video.muted = true;
          this.armUnmute();
          this.attemptPlay().catch(() => this.revealControls());
        });
      }

      // play() returns a promise in modern browsers and nothing in old ones.
      // Normalising it keeps the caller free of that check.
      attemptPlay() {
        const attempt = this.video.play();
        return attempt && typeof attempt.catch === 'function' ? attempt : Promise.resolve();
      }

      /**
       * Turn the sound on at the visitor's first interaction with the page.
       *
       * Sound cannot simply be switched on: unmuting a video that was allowed
       * to play only because it was silent gets the video paused instead. So
       * we wait for a gesture, which is what grants the permission.
       */
      armUnmute() {
        if (this.unmuteArmed) return;
        this.unmuteArmed = true;

        const onGesture = () => {
          CST_VIDEO_GESTURES.forEach((type) => document.removeEventListener(type, onGesture));
          // Never start making noise for a section that has scrolled away.
          if (this.shouldPlay && !this.video.paused) this.video.muted = false;
        };

        CST_VIDEO_GESTURES.forEach((type) =>
          document.addEventListener(type, onGesture, { passive: true })
        );
      }

      showControls() {
        this.video.controls = true;
      }

      hideControls() {
        // Controls that were revealed because playback failed, or because the
        // visitor asked for reduced motion, are their only way in. Keep them.
        if (this.controlsLocked) return;
        this.video.controls = false;
      }

      // Hand the visitor a real player, permanently.
      revealControls() {
        this.controlsLocked = true;
        this.video.controls = true;
        this.video.removeAttribute('aria-hidden');
        this.video.removeAttribute('tabindex');
      }
    }
  );
}
